import React from 'react';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import ReactQuill from 'react-quill';
import { Button } from 'react-bootstrap';

import actions from '../../../redux/actions';
import Workflow from './Workflow';
import { findingType, constantsType } from './types';
import { addOwner } from '../../../redux/actions/Findings';

const FindingActions = actions.Finding;
const MessageActions = actions.Message;

const editorFormats = [
  'font', 'bold', 'italic', 'underline', 'blockquote', 'code-block',
  'list',
  'indent',
];

const editorModules = {
  toolbar: [
    [{ font: [] }, 'bold', 'italic', 'underline', 'blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: true,
  },
};

/* eslint-disable no-unused-vars */
const Finding = ({
  onAddOwner,
  onToggleTextbox,
  onFieldChange,
  onSaveComment,
  onGetComments,
  constants,
  comments,
  finding: {
    analysis,
    category: categoryId,
    created,
    createdBy,
    id,
    jurisdiction,
    name,
    orgId,
    pendingAnswer,
    pendingMessage,
    priority,
    showingTextBox,
    type: typeId,
    status,
    workflows,
  },
}) => {
  const toggleTextbox = () => onToggleTextbox(id);

  const updatePendingMessage = debounce(value => onFieldChange({
    id,
    value,
    field: 'pendingMessage',
  }), 200);

  const cancelTextbox = () => {
    onFieldChange({ id, value: '', field: 'pendingMessage' });
    toggleTextbox();
  };

  const submitMessage = () => {
    onSaveComment({ message: pendingMessage, id });
    onFieldChange({ id, value: '', field: 'pendingMessage' });
    toggleTextbox();
  };

  const type = constants.type.fromId(typeId);
  const category = constants.category.fromId(categoryId);

  // This will add the close class and animate closing the finding once the
  // redux store marked the finding as resolved.
  const closed = status === constants.status.fromName('Resolved').id;

  // TODO ReactQuill could use a wrapper. Modules.toolbar needs to pass through
  // Also, the way dispatch functions are here could be fixed. No need to pass
  // findingId + UserID should be retrieved from store!

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <div className={`finding ${closed ? 'closed' : false}`}>
      <div className="flags">
        <div className={`type-${type.name.toLowerCase()}`}>
          {type.name[0]}
        </div>
        <div className={`priority-${priority}`}>
            P{priority}
        </div>
      </div>
      <div className="content">
        <div className="findingHeader">
          <div>{ name }</div>
          <div>{ showingTextBox }</div>
        </div>
        <div className="info">
          <div className="owners">
            <button
              type="button"
              onClick={() => onAddOwner({
                ownerId: window.settings.settings.user.id,
              })}
            >
              Add self as owner
            </button>

          </div>
          <div className="type">
            { category.name }
          </div>
          <div className="date">
            <div>{ moment(created).format('MMMM D') }</div>
          </div>
        </div>
        <div className="body">
          <div className="analysis">
            <div>{ analysis }</div>
          </div>
          <div className="workflowContainer">
            {
              // Only render uncompleted workflows
              workflows.filter(w => !w.completed).map((workflow, key) =>
                <Workflow
                  pendingAnswer={pendingAnswer}
                  key={key}
                  findingId={id}
                  workflow={workflow}
                />)
            }
          </div>
        </div>
        <div className="message">
          {
            showingTextBox ?
              <div>
                <ReactQuill
                  formats={editorFormats}
                  modules={editorModules}
                  placeholder=""
                  defaultValue={pendingMessage}
                  onChange={updatePendingMessage}
                  style={{
                    width: '100%',
                    backgroundColor: '#FFF',
                  }}
                />
                <br />
                <Button bsStyle='danger' onClick={cancelTextbox}>
                  Cancel
                </Button>
                <Button bsStyle='primary' onClick={submitMessage}>
                  Send
                </Button>
              </div> :
              <span className="placeholder" tabIndex="0" role="button" onClick={toggleTextbox}>
                Write a message...
              </span>
          }
        </div>
        <div className="message old">
          <button onClick={() => onGetComments({ id })} type="button">Get Comments</button>
          <span className="placeholder">
            { /* eslint-disable react/no-danger */
              comments.map(comment =>
                <div
                  key={comment.id}
                  className="comment"
                  dangerouslySetInnerHTML={{ __html: comment.body }}
                />)
              /* eslint-enable react/no-danger */
            }
          </span>
        </div>
      </div>
    </div>
  );
};

Finding.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  constants: constantsType.isRequired,
  finding: findingType.isRequired,
  onToggleTextbox: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSaveComment: PropTypes.func.isRequired,
  onGetComments: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  constants: state.findings.get('constants'),
  comments: state.message.getIn(['messages', ownProps.finding.id], []),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onToggleTextbox: id => dispatch(FindingActions.toggleTextbox({ id })),
  onFieldChange: ({ id, field, value }) =>
    dispatch(FindingActions.updateField({ id, field, value })),
  onSaveComment: ({ message, id }) => dispatch(MessageActions.saveCommentForFinding({
    body: message,
    findingId: id,
    personId: window.settings.settings.user.id,
  })),
  onGetComments: ({ id }) => dispatch(MessageActions.getCommentsForFinding({
    personId: window.settings.settings.user.id,
    findingId: id,
  })),
  onAddOwner: ({ ownerId }) => {
    dispatch(
      addOwner({
        findingId: ownProps.finding.id,
        personId: window.settings.settings.user.id,
        ownerId,
        ownerType: ownProps.role,
      })
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Finding);



// WEBPACK FOOTER //
// ./src/views/Components/Finding/Finding.js